import React from 'react';
import ReactMarkdown from 'react-markdown';
import './ChatMessageList.css';
import { AimieRobotAvatarIcon, UserAvatarIcon } from './ChatAvatarIcons';
import ContemplatingIndicator from './ContemplatingIndicator';
import StimulusAnnotationText from './oars/StimulusAnnotationText';

const preserveSingleLineBreaks = (text) => (text || '').replace(/\n/g, '  \n');

const MarkdownMessage = ({ text, className = '' }) => (
  <div className={className}>
    <ReactMarkdown
      components={{
        p: ({ children }) => <>{children}</>,
      }}
    >
      {preserveSingleLineBreaks(text)}
    </ReactMarkdown>
  </div>
);

function ChatMessageList({
  conversation,
  isClientMode,
  humanSpeaker = 'counsellor',
  viewedTurnIndex,
  highlightedTurnIndex = null,
  totalTurns,
  fullSessionAnnotations = [],
  skillsAnnotationsEnabled = true,
  suppressAnnotationTooltips = false,
  theme = 'dark',
  t,
  llmMode,
  llmPaused,
  llmFinishingUtterance,
  sessionEnded,
  isConsentPending,
  showBtcConsentCue,
  handleConsentChoice,
  userAvatarPreference,
  messagesContainerRef,
  messagesEndRef,
  turnAnchorRefs,
}) {
  // Which speaker sits on the user's side of the layout — in client mode their
  // own turns are the client ones. Kept separate from who actually authored the
  // turn: an llm_mode replay uses the same layout but has no human turns at all,
  // so `humanSpeaker` is null there and only the avatar changes.
  const ownSideSpeaker = isClientMode ? 'client' : 'counsellor';
  const isHumanAuthoredMessage = (speaker) => (
    humanSpeaker !== null && speaker === humanSpeaker
  );

  return (
    <div className="messages-container" ref={messagesContainerRef}>
      {conversation.map((msg, idx) => {
        const messageTurnIndex = isClientMode
          ? (idx < 2 ? -1 : Math.floor((idx - 2) / 2))
          : Math.floor(idx / 2);
        const isViewingLatest = viewedTurnIndex === null || (totalTurns > 0 && viewedTurnIndex === totalTurns - 1);
        const isActiveMessage = isViewingLatest || messageTurnIndex === viewedTurnIndex;
        const isHighlighted = highlightedTurnIndex !== null && messageTurnIndex === highlightedTurnIndex;
        const isAnchor = isClientMode ? (idx >= 2 && idx % 2 === 0) : idx % 2 === 0;
        const isHumanMessage = isHumanAuthoredMessage(msg.speaker);
        const isOwnSideMessage = msg.speaker === ownSideSpeaker;
        const visualSpeakerClass = isOwnSideMessage ? 'counsellor' : 'client';
        const turnAnnotations = skillsAnnotationsEnabled && msg.speaker === 'client'
          ? fullSessionAnnotations.filter((annotation) => annotation.turn_id === `turn_${idx}`)
          : [];

        return (
          <div
            key={idx}
            ref={isAnchor ? (el) => {
              if (el) {
                turnAnchorRefs.current[messageTurnIndex] = el;
              }
            } : null}
            className={`message ${visualSpeakerClass}${!isActiveMessage ? ' message-faded' : ''}${isHighlighted ? ' message-highlighted' : ''}`}
          >
            {!isOwnSideMessage && (
              <span className="message-avatar message-avatar--robot" aria-hidden="true">
                <AimieRobotAvatarIcon />
              </span>
            )}
            <div className="message-bubble-shell">
              {msg.speaker === 'counsellor' ? (
                isClientMode
                  ? (msg.isStreaming && !msg.text)
                    ? <ContemplatingIndicator />
                    : <MarkdownMessage className={`message-text-plain message-text-plain--markdown${msg.isStreaming ? ' streaming' : ''}`} text={msg.text} />
                  : <MarkdownMessage className="message-text message-text--markdown" text={msg.text} />
              ) : (
                isClientMode
                  ? <div className="message-text">{msg.text}</div>
                  : (msg.isStreaming && !msg.text)
                    ? <ContemplatingIndicator />
                    : turnAnnotations.length > 0 && !msg.isStreaming
                      ? <StimulusAnnotationText
                          text={msg.text}
                          annotations={turnAnnotations}
                          theme={theme}
                          t={t}
                          suppressed={suppressAnnotationTooltips}
                        />
                      : <div className={`message-text-plain${msg.isStreaming ? ' streaming' : ''}`}>{msg.text}</div>
              )}
            </div>
            {isOwnSideMessage && (
              <span
                className={`message-avatar message-avatar--${isHumanMessage ? 'human' : 'robot'}`}
                aria-hidden="true"
              >
                {isHumanMessage
                  ? <UserAvatarIcon {...userAvatarPreference} />
                  : <AimieRobotAvatarIcon />}
              </span>
            )}
          </div>
        );
      })}
      {isConsentPending && conversation.length > 0 && (
        <div className="btc-consent-inline-container">
          <div
            className={`consent-panel${showBtcConsentCue ? ' consent-panel--selection-cue' : ''}`}
            data-guided-highlight-purpose={showBtcConsentCue ? 'Choose whether to give consent and begin this session.' : undefined}
          >
            <span className="consent-label">Give consent</span>
            <div className="consent-divider" />
            <div className="btc-consent-inline-actions" role="group" aria-label="Consent choice">
              <button
                type="button"
                className="consent-btn consent-btn-yes"
                onClick={() => handleConsentChoice('yes')}
              >
                Yes
              </button>
              <button
                type="button"
                className="consent-btn consent-btn-no"
                onClick={() => handleConsentChoice('no')}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const lastMessage = conversation[conversation.length - 1];
        const clientIsStreaming = lastMessage?.speaker === 'client' && lastMessage?.isStreaming;
        const showIndicator =
          llmMode &&
          !sessionEnded &&
          (!llmPaused || llmFinishingUtterance) &&
          !clientIsStreaming;
        if (!showIndicator) return null;

        const nextSpeaker = !lastMessage
          ? 'counsellor'
          : lastMessage.speaker === 'counsellor'
            ? 'client'
            : 'counsellor';

        return (
          <div className={`message ${nextSpeaker}`}>
            {nextSpeaker === 'client' && (
              <span className="message-avatar message-avatar--robot" aria-hidden="true">
                <AimieRobotAvatarIcon />
              </span>
            )}
            <div className={`message-bubble-shell message-bubble-shell--thinking-llm${nextSpeaker === 'counsellor' ? ' message-bubble-shell--thinking-llm-counsellor' : ''}`}>
              <ContemplatingIndicator />
            </div>
            {nextSpeaker === 'counsellor' && (
              <span className="message-avatar message-avatar--human" aria-hidden="true">
                <UserAvatarIcon {...userAvatarPreference} />
              </span>
            )}
          </div>
        );
      })()}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default ChatMessageList;
